import React from "react";
import { Box, Card, Grid, Typography } from "@mui/joy";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { EmailCampaignStats } from "../../../types/marketing";

const Metric: React.FC<{ label: string; value: string | number }> = ({
  label,
  value,
}) => (
  <Card variant="soft" sx={{ p: 1.5, textAlign: "center" }}>
    <Typography level="h3">{value}</Typography>
    <Typography level="body-xs">{label}</Typography>
  </Card>
);

export const CampaignStatsCharts: React.FC<{ stats: EmailCampaignStats }> = ({
  stats,
}) => {
  const t = stats.totals;
  const funnel = [
    { name: "Sent", value: t.sent },
    { name: "Delivered", value: t.delivered },
    { name: "Opened", value: t.opened },
    { name: "Clicked", value: t.clicked },
  ];

  return (
    <Box>
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid xs={6} md={3}>
          <Metric label="Recipients" value={t.total_recipients} />
        </Grid>
        <Grid xs={6} md={3}>
          <Metric label="Sent" value={t.sent} />
        </Grid>
        <Grid xs={6} md={3}>
          <Metric label="Open rate" value={`${stats.rates.open_rate}%`} />
        </Grid>
        <Grid xs={6} md={3}>
          <Metric label="Click rate" value={`${stats.rates.click_rate}%`} />
        </Grid>
        <Grid xs={6} md={3}>
          <Metric label="Bounced" value={t.bounced} />
        </Grid>
        <Grid xs={6} md={3}>
          <Metric label="Unsubscribed" value={t.unsubscribed} />
        </Grid>
        <Grid xs={6} md={3}>
          <Metric label="Complained" value={t.complained} />
        </Grid>
        <Grid xs={6} md={3}>
          <Metric label="Skipped (limit)" value={t.skipped} />
        </Grid>
      </Grid>

      <Typography level="title-sm" sx={{ mb: 1 }}>
        Engagement funnel
      </Typography>
      <Box sx={{ height: 260, mb: 3 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={funnel}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Bar dataKey="value" fill="#0084ff" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {stats.timeline.length > 0 && (
        <>
          <Typography level="title-sm" sx={{ mb: 1 }}>
            Activity over time
          </Typography>
          <Box sx={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.timeline}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="sent"
                  stroke="#0084ff"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="opened"
                  stroke="#1abc9c"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="clicked"
                  stroke="#e67e22"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </>
      )}
    </Box>
  );
};
